import React, { useState, useEffect, useRef } from 'react';
import Card from '../../modules/contractor-request-a-quote-form/card';
import StepForm from './step1-form';
import StepReviews from './step2-reviews';
import Stars from './stars';
import {
  RequestAQuoteFormProps,
  CardProps,
  StepTermsProps,
  FormDataProp,
  FormProp,
  ModalProps
} from './types';
import { getStoredGeolocationData } from '../../../helpers/geolocation';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  COOKIE_NAMES,
  GeolocationType,
  USER_PROFILE_KEYS,
  USER_PROFILE_OBJECT_KEY,
  secureStorage
} from '../../../helpers/global-storage';
import { ExtendedWindowType } from '../../engine';

const urlParams = new URLSearchParams(window.location.search);

const Form: React.FC<RequestAQuoteFormProps> = ({
  formLabels,
  thankYouLabels,
  certificationsCardLabel,
  viewAllCertificationsLabel,
  certificationsModalId,
  showDefaultContractor,
  stepForm,
  stepReviews,
  stepTerms,
  reviewsName,
  termsConditionsName,
  endpointPost,
  modal,
  placeholderImg,
  contractorId,
  contractorType,
  contractor
}) => {
  const {
    heading,
    ofSteps,
    progressBarAria,
    backBtn,
    continueBtn,
    submitBtn,
    errorRequired,
    addressError,
    dividerStates,
    errorLabel,
    defaultLabel,
    defaultContractorCopy,
    disclaimer
  } = formLabels ? JSON.parse(formLabels) : '';
  const thankYouCardLabels: CardProps = thankYouLabels
    ? JSON.parse(thankYouLabels)
    : '';

  const stepFormLabels: FormProp[] = stepForm ? JSON.parse(stepForm) : '';
  const stepReviewsLabels: CardProps = stepReviews
    ? JSON.parse(stepReviews)
    : '';
  const stepTermsLabels: StepTermsProps = stepTerms
    ? JSON.parse(stepTerms)
    : '';
  const contractorObject = contractor as ModalProps;

  const userObjectData: string[] = [
    USER_PROFILE_KEYS.userName,
    USER_PROFILE_KEYS.userLastName,
    USER_PROFILE_KEYS.userEmail,
    USER_PROFILE_KEYS.userPhone,
    USER_PROFILE_KEYS.userMessage
  ];

  const userGeolocationData = [
    'zip',
    'latitude',
    'longitude',
    'country',
    'address',
    'city',
    'state',
    'street',
    'secondStreet'
  ];

  const [step, setStep] = useState<number>(0);
  const [formData, setFormData] = useState<FormDataProp>();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [, setContractorSelected] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<boolean>(false);
  const card = useRef<HTMLDivElement | null>(null);
  const [fixedHeight, setFixedHeight] = useState<number>(0);
  const [utmValues, setUtmValues] = useState<string>('');

  const styleRequestAQuote: { [key: string]: string } = {
    '--request-a-quote-fixed': `${fixedHeight}px`
  };

  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    const geolocationObject: GeolocationType = {
      address: '',
      zip: '',
      latitude: undefined,
      longitude: undefined,
      country: ''
    };
    const geolocation = getStoredGeolocationData();
    if (geolocation?.address) {
      geolocationObject.address = geolocation?.address
        ? geolocation.address
        : '';
      geolocationObject.zip = geolocation?.zip ? geolocation.zip : '';
      geolocationObject.latitude = geolocation?.latitude
        ? geolocation.latitude
        : undefined;
      geolocationObject.longitude = geolocation?.longitude
        ? geolocation.longitude
        : undefined;
      geolocationObject.country = geolocation?.country;
    }

    const formDataObj: FormDataProp = stepFormLabels.reduce(
      (obj: object, el: FormProp) => {
        Object.assign(obj, { [el.name]: '' });
        if (el.name === 'address') {
          Object.assign(obj, geolocationObject);
        }
        return obj;
      },
      {}
    );

    const userGeolocationDataStored = secureStorage.get(
      COOKIE_NAMES.geolocation
    );
    if (userGeolocationDataStored) {
      const userLocationInfo = JSON.parse(userGeolocationDataStored);
      userGeolocationData.forEach((dataName: string) => {
        if (userLocationInfo[dataName]) {
          formDataObj[dataName] = userLocationInfo[dataName]
            ? userLocationInfo[dataName]
            : '';
        }
      });
    }

    const userDataStored = secureStorage.get(USER_PROFILE_OBJECT_KEY);

    if (userDataStored) {
      const userDataInfo = JSON.parse(userDataStored);
      userObjectData.forEach((dataName: string) => {
        if (userDataInfo[dataName]) {
          formDataObj[dataName] = userDataInfo[dataName]
            ? userDataInfo[dataName]
            : '';
        }
      });
    }

    Object.assign(formDataObj, { [reviewsName]: [] });
    Object.assign(formDataObj, { [termsConditionsName]: false });

    setFormData(formDataObj);

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleResize = () => {
    if (card.current) {
      const header = (card.current as HTMLElement).querySelector(
        '.request-a-quote__header'
      );
      const footer = (card.current as HTMLElement).querySelector(
        '.request-a-quote__footer'
      );
      if (header && footer) {
        setFixedHeight(
          (header as HTMLElement)?.offsetHeight +
            (footer as HTMLElement)?.offsetHeight
        );
      }
    }
  };

  useEffect(() => {
    handleResize();
  }, [card.current]);

  useEffect(() => {
    if (urlParams.has('utm_source')) {
      const allParams = Array.from(urlParams.entries())
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
      setUtmValues(allParams);
    }
  }, []);

  const handleNextButton = async () => {
    if (step < 1) {
      setStep(step => step + 1);
    } else {
      setSubmitting(true);

      const captchaToken = await executeRecaptcha?.('quote');

      if (formData) {
        formData.token = captchaToken;
        const formDataArray = Object.entries(formData);
        const sessionUserData = formDataArray.reduce((sessionObj, dataName) => {
          if (userObjectData.includes(dataName[0] as string)) {
            (sessionObj as FormDataProp)[dataName[0]] = dataName[1];
          }
          return sessionObj;
        }, {});
        const sessionUserGeolocation = formDataArray.reduce(
          (sessionObj, dataName) => {
            if (userGeolocationData.includes(dataName[0] as string)) {
              (sessionObj as FormDataProp)[dataName[0]] = dataName[1];
            }
            return sessionObj;
          },
          {}
        );

        if (utmValues) {
          formData.UTM__c = utmValues;
        }

        secureStorage.set(
          USER_PROFILE_OBJECT_KEY,
          JSON.stringify(sessionUserData)
        );
        secureStorage.set(
          COOKIE_NAMES.geolocation,
          JSON.stringify(sessionUserGeolocation)
        );
      }

      fetch(endpointPost, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          contractorId: modal ? modal.id : contractorId,
          contractorType: modal ? modal.type : contractorType
        })
      })
        .then(response => response.json())
        .then(data => {
          if (!data.success) {
            setSubmitError(true);
          } else {
            setSubmitError(false);
            (window as ExtendedWindowType).dataLayer.push({
              event: 'contractor_leads',
              event_category: 'find a contractor',
              event_action: 'form success',
              event_attributes: {
                contractor_id: modal ? modal.id : contractorObject?.id,
                contractor_name: modal
                  ? modal.contractorName
                  : contractorObject?.contractorName,
                contractor_certificates_count: modal
                  ? modal.numberOfCertificates
                  : contractorObject?.numberOfCertificates,
                contractor_rating: modal
                  ? modal.rating
                  : contractorObject?.rating,
                contractor_reviews_count: modal
                  ? modal.numberOfReviews
                  : contractorObject?.numberOfReviews,
                contractor_certificate_name: modal
                  ? modal.certificateName
                  : contractorObject?.certificateName,
                contractor_type: modal ? modal.type : contractorObject?.type,
                form_name: 'request a quote form'
              }
            });
          }
        })
        .catch(error => {
          setSubmitError(true);
          console.error('Error:', error);
        })
        .finally(() => {
          setSubmitting(false);
          setStep(step => step + 1);
        });
    }
  };

  const handleBackButton = () => {
    setStep(step => step - 1);
  };

  const renderSteps = () => {
    switch (step) {
      case 0:
        return (
          <StepForm
            inputs={stepFormLabels}
            errorMessage={errorRequired}
            addressErrorMessage={addressError}
            setFormData={setFormData}
            formData={formData as FormDataProp}
            setIsFormValid={setIsFormValid}
            isFormValid={isFormValid}
            isModal={modal ? true : false}
            contractorData={modal ? modal : contractorObject}
            twoColumns={modal ? true : false}
          />
        );

      case 1:
        return (
          <StepReviews
            headingReviews={stepReviewsLabels.heading}
            copyReviews={stepReviewsLabels.copy}
            dividerStates={dividerStates}
            isModal={modal ? true : false}
            {...stepTermsLabels}
            setFormData={setFormData}
            formData={formData as FormDataProp}
            reviewName={reviewsName}
            contractorId={modal ? modal.id : contractorId}
            showDefaultContractor={showDefaultContractor}
            defaultContractor={modal ? modal : contractorObject}
            disclaimerHeading={stepTermsLabels.heading}
            disclaimer={disclaimer}
            defaultLabel={defaultLabel}
            defaultContractorCopy={defaultContractorCopy}
            setContractorSelected={setContractorSelected}
            termsConditionsName={termsConditionsName}
            certificationsCardLabel={certificationsCardLabel}
            viewAllCertificationsLabel={viewAllCertificationsLabel}
            certificationsModalId={certificationsModalId}
          />
        );

      default:
        return <></>;
    }
  };

  return (
    <>
      {step < 2 ? (
        <div ref={card} style={styleRequestAQuote}>
          <div className="request-a-quote__header">
            <h2
              className={`request-a-quote__heading ${
                modal ? 'request-a-quote__heading--modal' : ''
              }`}
            >
              {heading}
            </h2>

            {modal && step < 1 && (
              <div className="request-a-quote-modal__contractor">
                <img
                  className="request-a-quote-modal__c-img"
                  alt={modal.imageAlt}
                  src={modal.image ? modal.image : placeholderImg}
                  width="120"
                  height="80"
                />
                <div className="request-a-quote-modal__c-content">
                  <h3 className="request-a-quote-modal__c-heading">
                    {modal.contractorDBA
                      ? modal.contractorDBA
                      : modal.contractorName}
                  </h3>
                  {modal.rating ? (
                    <Stars
                      rating={modal.rating}
                      numberOfReviews={modal.numberOfReviews}
                    />
                  ) : null}
                  <span className='request-a-quote-modal__c-location'>
                    {modal.city}
                    {modal.city ? ', ' : ''}
                    {modal.state}
                    {modal.state ? ' - ' : ''}
                    {modal.distanceinmiles?.toFixed(1)}
                    {modal.distanceinmiles ? ' mi' : ''}
                  </span>
                </div>
              </div>
            )}
            <p
              className="request-a-quote__progress-indicator"
              aria-label={progressBarAria}
            >{`${step + 1}${ofSteps}`}</p>
            <div
              className="request-a-quote__progress-bar"
              data-step={step + 1}
              role="presentation"
            ></div>
          </div>

          {formData && renderSteps()}

          <div
            className={`request-a-quote__footer ${
              modal ? 'request-a-quote__footer--modal' : ''
            }`}
          >
            {step > 0 ? (
              <button
                className="link link--arrow-back"
                onClick={() => handleBackButton()}
              >
                <span>{backBtn}</span>
              </button>
            ) : (
              <div></div>
            )}
            <div className="request-a-quote__submit">
              <button
                className="btn btn--secondary"
                disabled={
                  step === 1 && formData
                    ? (!formData[termsConditionsName] && !submitting)
                    : !isFormValid
                }
                onClick={() => handleNextButton()}
              >
                {step === 1 ? submitBtn : continueBtn}
              </button>
              {submitError && (
                <span className="request-a-quote__submit-error">
                  {errorLabel}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <Card
          {...thankYouCardLabels}
          submitError={submitError}
          errorLabel={errorLabel}
        />
      )}
    </>
  );
};

export default Form;
